import React, { useEffect, useRef } from 'react';
import { Truck } from 'lucide-react';
import { formatNumber, UnitSystem, UNITS } from '../utils/calculationUtils';

interface CalculatorResultsProps {
  tonnage: number;
  hasCalculated: boolean;
  unit: UnitSystem;
}

export const CalculatorResults: React.FC<CalculatorResultsProps> = ({ 
  tonnage, 
  hasCalculated,
  unit
}) => {
  const resultRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasCalculated && resultRef.current) {
      resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [hasCalculated, tonnage]);

  if (!hasCalculated) {
    return null;
  }

  const metricTons = unit === UNITS.IMPERIAL ? tonnage * 0.907185 : tonnage;

  return (
    <div 
      ref={resultRef}
      className="bg-slate-50 border border-slate-200 rounded-lg p-6 animate-fade-in"
    >
      <div className="flex items-center mb-4">
        <Truck className="h-6 w-6 text-blue-600 mr-2" />
        <h3 className="text-lg font-semibold text-slate-800">Calculation Results</h3>
      </div>
      
      <div className="mb-4">
        <p className="text-slate-600 mb-2">Asphalt Required:</p>
        <div className="bg-white border border-slate-200 rounded-md px-4 py-3">
          <p className="text-2xl font-bold text-blue-700">
            {formatNumber(unit === UNITS.IMPERIAL ? tonnage : metricTons, 2)} {unit === UNITS.IMPERIAL ? 'US tons' : 'metric tonnes'}
          </p>
          <p className="text-lg text-slate-600 mt-1">
            ({unit === UNITS.IMPERIAL ? `${formatNumber(metricTons, 2)} metric tonnes` : `${formatNumber(tonnage, 2)} US tons`})
          </p>
        </div>
      </div>
      
      <div className="text-sm text-slate-600">
        <p className="mb-2">
          <span className="font-semibold">Equivalent to approximately:</span>
        </p>
        <ul className="space-y-1 pl-5 list-disc">
          <li>{Math.round(tonnage * 2000)} pounds ({Math.round(metricTons * 1000)} kilograms)</li>
          <li>{Math.ceil(tonnage / 20)} standard truckloads (assuming 20-ton capacity)</li>
          <li>{formatNumber(tonnage * 0.45, 2)} cubic yards ({formatNumber(metricTons * 0.34, 2)} cubic meters)</li>
        </ul>
      </div>
    </div>
  );
};